import { useState, useEffect } from "react";
// Removed react-router-dom imports
import { Product } from "@/models/Product";
import { useCart } from "@/context/CartContext";
import { useNavigation } from "@/context/NavigationContext"; // Added for state-based nav
import { aircraftMock } from "@/mock/aircraftMock";
import { partsMock } from "@/mock/partsMock";
import { equipmentMock } from "@/mock/equipmentMock";
import { experiencesMock } from "@/mock/experiencesMock";
import { coursesMock } from "@/mock/coursesMock";
import { Button } from "@/components/ui/button";
import { ShoppingCart, MessageCircle, ArrowLeft, MapPin, User } from "lucide-react";
import Loader from "@/components/Loader";
import { toast } from "sonner";

// Mock data aggregation
const allProducts: Product[] = [...aircraftMock, ...partsMock, ...equipmentMock, ...experiencesMock, ...coursesMock];

const ProductDetails = () => {
  // Use navigation context instead of router hooks
  const { params, navigateTo } = useNavigation();
  const id = params?.id;

  const { addToCart } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate data fetching
    const timer = setTimeout(() => {
      setProduct(allProducts.find(p => p.id === id) || null);
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [id]);

  if (loading) return <Loader />;

  if (!product) return (
    <div className="container py-20 text-center animate-fade-in">
      <p className="text-xl text-muted-foreground font-display">Produsul nu a fost găsit.</p>
      <Button
        variant="outline"
        className="mt-4 border-primary/30"
        onClick={() => navigateTo("home")}
      >
        Înapoi la Acasă
      </Button>
    </div>
  );

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("ro-RO", { style: "currency", currency: "EUR" }).format(price);

  const handleAddToCart = () => {
    addToCart(product);
    toast.success("Produs adăugat în coș!");
  };

  return (
    <div className="container py-8 animate-fade-in">
      {/* Back button using state navigation */}
      <button
        onClick={() => navigateTo("home")}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors group"
      >
        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" /> Înapoi
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Image Container */}
        <div className="aviation-glass rounded-2xl overflow-hidden aspect-[4/3] border border-white/10 shadow-xl">
          <img
            src={product.images[0]}
            alt={product.title}
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          />
        </div>

        {/* Info Container */}
        <div className="space-y-6">
          <div className="space-y-1">
            <span className="text-xs uppercase tracking-[0.2em] text-primary font-display font-bold">
              {product.category} · {product.subcategory}
            </span>
            <h1 className="text-4xl font-display font-black tracking-tight mt-2 uppercase italic">{product.title}</h1>
          </div>

          <p className="text-4xl font-display font-bold text-accent">
            {formatPrice(product.price)}
          </p>

          <p className="text-muted-foreground leading-relaxed text-lg font-light">
            {product.description}
          </p>

          {/* Specs Table */}
          <div className="aviation-glass rounded-2xl p-6 space-y-4 border border-white/5 bg-white/5">
            <h3 className="font-display font-bold text-xs uppercase tracking-[0.2em] text-muted-foreground">Specificații Tehnice</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-orange-500" />
                <span className="text-muted-foreground">Locație:</span>
                <span className="text-foreground font-medium">{product.location}</span>
              </div>
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-orange-500" />
                <span className="text-muted-foreground">Vânzător:</span>
                <span className="text-foreground font-medium">{product.sellerName}</span>
              </div>

              {/* Dynamic Type Casting for Mock Data Props */}
              {"year" in product && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground">An:</span>
                  <span className="text-white font-medium">{(product as any).year}</span>
                </div>
              )}
              {"flightHours" in product && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground">Ore zbor:</span>
                  <span className="text-white font-medium">{(product as any).flightHours}h</span>
                </div>
              )}
              {"condition" in product && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground">Stare:</span>
                  <span className="text-white font-medium capitalize">{(product as any).condition}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <Button
              size="lg"
              className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90 font-display font-bold gap-3 rounded-xl shadow-lg shadow-accent/20"
              onClick={handleAddToCart}
            >
              <ShoppingCart className="h-5 w-5" /> Adaugă în coș
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="flex-1 border-primary/30 hover:bg-primary/10 font-display font-bold gap-3 rounded-xl"
            >
              <MessageCircle className="h-5 w-5" /> Contactează Vânzătorul
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;